import React, { PropsWithChildren } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PARAM, ROUTE } from 'client_react/booking/common';
import { ContractViewPage, EmailVerificationPage } from 'client_react/contract';
import { PARAM as CONTRACT_PARAM, ROUTE as CONTRACT_ROUTE } from 'client_react/contract/common';
import {
    BookingQuestionnairePage,
    ClientBookingApp,
    ConfirmationPage,
    ContactInformationPage,
    ContractPage,
    DateAndTimePage,
    PaymentPage
} from './booking';
import ClientBookingExperience from './booking/ClientBookingExperience';
import SessionPage from './booking/SessionPage';
import {
    AboutPage,
    BookingPage,
    App as BrandApp,
    CategoryPage,
    ContactPage,
    IndexPage,
    NotFoundPage,
    UnsubscribePage
} from './brand';
import { EventCategoriesDataProvider } from './brand/data/eventCategoriesData';
import { EventsDataProvider } from './brand/data/eventsData';
import { ContractApp } from './contract';
import { GlobalStyles } from './global';
import { InvoiceApp, InvoiceLandingPage, InvoicePaymentPage } from './invoice';
import { QuestionnaireApp } from './questionnaire';

const EventsAndEventCategoriesDataProvider = ({ children }: PropsWithChildren) => (
    <EventsDataProvider>
        <EventCategoriesDataProvider>{children}</EventCategoriesDataProvider>
    </EventsDataProvider>
);

interface IClientAppProps {
    gRecaptchaKey: string;
}

const ClientApp: React.FC<IClientAppProps> = ({ gRecaptchaKey }) => {
    return (
        <>
            <GlobalStyles />
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<BrandApp />}>
                        <Route path="contact" element={<ContactPage />} />
                        <Route path="about" element={<AboutPage />} />
                        <Route path="booking" element={<BookingPage />} />
                        <Route
                            path="category/:categoryId"
                            element={
                                <EventsAndEventCategoriesDataProvider>
                                    <CategoryPage />
                                </EventsAndEventCategoriesDataProvider>
                            }
                        />
                        <Route
                            index
                            element={
                                <EventsAndEventCategoriesDataProvider>
                                    <IndexPage />
                                </EventsAndEventCategoriesDataProvider>
                            }
                        />
                    </Route>
                    <Route element={<ClientBookingApp />}>
                        <Route
                            path={`/booking/:${PARAM.SESSION_TYPE_SLUG}`}
                            element={<ClientBookingExperience />}
                        >
                            <Route
                                path={ROUTE.DATE_AND_TIME}
                                element={<DateAndTimePage recaptchaKey={gRecaptchaKey} />}
                            />
                            <Route
                                path={ROUTE.CONTACT_INFORMATION}
                                element={<ContactInformationPage />}
                            />
                            <Route
                                path={ROUTE.QUESTIONNAIRE}
                                element={<BookingQuestionnairePage />}
                            />
                            <Route path={ROUTE.CONTRACT} element={<ContractPage />} />
                            <Route
                                path={ROUTE.CONTRACT_CONFIRMATION}
                                element={<ContractPage isConfirmation />}
                            />
                            <Route path={ROUTE.PAYMENT} element={<PaymentPage />} />
                            <Route path={ROUTE.CONFIRMATION} element={<ConfirmationPage />} />
                            <Route
                                index
                                element={<DateAndTimePage recaptchaKey={gRecaptchaKey} />}
                            />
                        </Route>
                        <Route
                            path={`/session/:${PARAM.BOOKING_SESSION_ID}`}
                            element={<SessionPage />}
                        />
                    </Route>
                    <Route
                        path={`/${CONTRACT_ROUTE.CONTRACT}/:${CONTRACT_PARAM.CONTRACT_PUBLIC_ID}`}
                        element={<ContractApp />}
                    >
                        <Route path={CONTRACT_ROUTE.EMAIL} element={<EmailVerificationPage />} />
                        <Route path={CONTRACT_ROUTE.VIEW} element={<ContractViewPage />} />
                        <Route index element={<EmailVerificationPage />} />
                    </Route>
                    <Route path="/invoice" element={<InvoiceApp />}>
                        <Route path=":invoiceId" element={<InvoiceLandingPage />} />
                        <Route path=":invoiceId/payment" element={<InvoicePaymentPage />} />
                    </Route>
                    <Route
                        path="/questionnaire/:questionnairePublicId"
                        element={<QuestionnaireApp />}
                    />
                    <Route path="unsubscribe" element={<UnsubscribePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </BrowserRouter>
        </>
    );
};

export default ClientApp;
